import { isApiEnabled } from '@muzly/api-client';

import { deleteAnaly, listAnalyPage } from './analyTransport';
import { removeDiagnosis } from '../record';

import type { AnalyView } from './analyContract';
import type { TransportResult } from './analySession';

/**
 * purgeDiagnoses — 서버에 있는 내 회차를 전부 `DELETE /analy/{id}` 로 지운다(MY "진단 데이터
 * 모두 삭제"). 로컬 정리는 하지 않는다 — **어느 회차가 서버에서 사라졌는지**만 돌려주고,
 * 그 결과로 무엇을 비울지는 부르는 쪽이 정한다(일부 실패면 성공분만, 전부 성공이면 전부).
 *
 * 서버 IO 는 deps 로 주입한다(`restoreDiagnoses` 와 같은 모양) — 순회·판정만 이 파일의 일이다.
 *
 * - **목록을 먼저 끝까지 모으고 나서 지운다.** 지우면서 읽으면 페이지가 밀려 건너뛴다. 목록
 *   조회가 한 페이지라도 실패하면 아무것도 지우지 않는다(`listFailed`).
 * - **종료는 "새 id 가 없는 페이지"다.** `total` 은 하위 제출까지 세므로 메인 수와 다르다. 서버가 `page`
 *   를 무시해 같은 목록을 되풀이해도 끝나도록 새 id 가 하나도 없는 페이지에서도 멈춘다.
 * - **메인 id 만 보낸다.** 서버가 하위 id 는 거절하고(`assertMainId`), list 는 하위를 섞어 온다.
 * - **404 는 성공이다.** 이미 지운 회차다 — 다시 눌렀을 때 남은 것만 지우는 재시도가 이걸로 선다.
 */

/** 서버가 `page` 를 무시하거나 끝없이 돌려줄 때를 위한 안전 상한. 넘으면 목록 실패다. */
const MAX_PAGES = 100;

export interface PurgeDeps {
  enabled: () => boolean;
  /** 0부터(`collectMainIds`). 실패·세션 미확보는 `null`. */
  listPage: (page: number) => Promise<AnalyView[] | null>;
  deleteAnaly: (id: string) => Promise<TransportResult>;
}

/** id 는 서버 메인 id. */
export interface PurgeResult {
  deleted: string[];
  failed: string[];
  /** API 가 꺼져 서버를 부르지 않았다 — 부르는 쪽은 로컬만 비운다. */
  apiOff: boolean;
  /** 목록을 못 모았다 — 서버·로컬 어느 쪽도 지우지 않았다. */
  listFailed: boolean;
}

const defaultDeps = (): PurgeDeps => ({
  enabled: isApiEnabled,
  listPage: listAnalyPage,
  deleteAnaly,
});

const NOTHING = { deleted: [], failed: [], apiOff: false, listFailed: false };

const isMain = (view: AnalyView): view is AnalyView & { id: string } =>
  Boolean(view.id) && (view.stereo === undefined || view.stereo === '');

/**
 * 메인 id 를 전부 모은다. 조회가 한 번이라도 실패하거나 상한까지 끝나지 않으면 `null` — 못 끝낸
 * 목록을 "다 모았다" 로 읽으면 서버에 남긴 채 로컬을 비운다.
 *
 * 페이지 번호 규칙은 앱에서 확인하지 못했다(미확인 — 백엔드는 기본 0 이지만 실제 규칙은
 * lemon-core 가 정한다). 0 번이 비어 오는 서버(1부터 세고 0 은 빈 목록)도, 0 번을 1 번으로
 * 보정하는 서버(0 번과 1 번이 같은 페이지)도 있을 수 있어서 **0·1 번에서는 "새 id 없음"으로
 * 끝내지 않는다** — 2 번부터만 끝낸다. 그 대신 0부터 세는 서버에서도 1 번은 항상 읽는다.
 */
const collectMainIds = async (deps: PurgeDeps): Promise<string[] | null> => {
  const ids = new Set<string>();
  const seen = new Set<string>();
  for (let page = 0; page <= MAX_PAGES; page += 1) {
    let views: AnalyView[] | null;
    try {
      views = await deps.listPage(page);
    } catch {
      return null;
    }
    if (views === null) {
      return null;
    }
    const before = seen.size;
    for (const view of views) {
      if (view.id) {
        seen.add(view.id);
      }
      // 이미 지워진 행은 다시 지우지 않는다(서버가 숨기는지는 미확인). `seen` 에는 넣어 둔다 —
      // 지워진 행뿐인 페이지를 "새 id 없음" 으로 읽어 끝내면 안 된다.
      if (isMain(view) && !view.deletedAt) {
        ids.add(view.id);
      }
    }
    // 처음 보는 id 가 하나도 없는 페이지(빈 페이지 포함)는 끝이다 — 서버가 `page` 를 무시해
    // 같은 목록을 되풀이해도 끝난다. 하위만 있는 페이지는 새 id 가 있으니 계속 간다.
    // 0·1 번은 서로 같은 페이지일 수 있어 끝 신호로 쓰지 않는다(위 주석).
    if (page >= 2 && seen.size === before) {
      return [...ids];
    }
  }
  return null;
};

export const purgeDiagnoses = async (
  deps: PurgeDeps = defaultDeps(),
): Promise<PurgeResult> => {
  if (!deps.enabled()) {
    return { ...NOTHING, apiOff: true };
  }
  const ids = await collectMainIds(deps);
  if (ids === null) {
    return { ...NOTHING, listFailed: true };
  }
  const deleted: string[] = [];
  const failed: string[] = [];
  for (const id of ids) {
    let result: TransportResult;
    try {
      result = await deps.deleteAnaly(id);
    } catch {
      result = { ok: false };
    }
    if (result.ok || result.status === 404) {
      deleted.push(id);
    } else {
      failed.push(id);
    }
  }
  return { deleted, failed, apiOff: false, listFailed: false };
};

/** 회차 삭제에 필요한 것만 — 로컬 id 와 서버 메인 id(`analyId`, 옛 레코드엔 없다). */
export interface DeletableRecord {
  id: string;
  analyId?: string;
}

export interface DeleteDiagnosisDeps {
  enabled: () => boolean;
  deleteAnaly: (id: string) => Promise<TransportResult>;
  removeDiagnosis: (id: string) => void;
}

const defaultDeleteDeps = (): DeleteDiagnosisDeps => ({
  enabled: isApiEnabled,
  deleteAnaly,
  removeDiagnosis,
});

/**
 * 삭제 버튼을 세울지 — API 가 꺼져 있으면(로컬뿐인 환경) 늘 서고, 켜져 있으면 서버 사본을
 * 가리킬 `analyId` 가 있는 레코드만 선다. 옛 레코드를 로컬만 지우면 서버 사본이 조용히 남는다
 * (전체 삭제는 서버 목록이 기준이라 옛 회차도 지운다).
 */
export const canDeleteDiagnosis = (
  record: DeletableRecord | null,
  apiEnabled: boolean = isApiEnabled(),
): boolean => record !== null && (!apiEnabled || Boolean(record.analyId));

/**
 * 회차 하나를 지운다 — **서버가 먼저, 성공해야 로컬이다**(404 는 성공). 서버가 실패하면 로컬
 * 레코드를 남겨 다시 시도할 수 있게 한다. API 가 꺼져 있으면 로컬만 지운다.
 */
export const deleteDiagnosis = async (
  record: DeletableRecord,
  deps: DeleteDiagnosisDeps = defaultDeleteDeps(),
): Promise<'deleted' | 'failed'> => {
  if (deps.enabled()) {
    if (!record.analyId) {
      return 'failed';
    }
    let result: TransportResult;
    try {
      result = await deps.deleteAnaly(record.analyId);
    } catch {
      return 'failed';
    }
    if (!result.ok && result.status !== 404) {
      return 'failed';
    }
  }
  deps.removeDiagnosis(record.id);
  return 'deleted';
};
